import { createSelector } from "reselect";
import {
  Device,
  DeviceData,
  DeviceFilter,
  DeviceFilterMap,
  DevicesSorting,
  DevicesSortType,
  DeviceType,
} from ".";

import {
  ActivityStatus,
  AppStateDevicesStatus,
  BedStatusValue,
  DevicesStatusMap,
  OccupancyStatus,
  OnOffStatus,
  PowerStatus,
  SensorsStatus,
  UserNearbyStatus,
  VITALS_LOW_RSSI,
  WhyNotActive,
  WifiStatus,
} from "../devicesStatuses";
import { OrganizationNode } from "../organization";
import {
  currentUserBaseNode,
  nodeById,
  organizationNodes,
  pathToNode,
  userMonitorNodeId,
  userSelectedENDNodeId,
  userSelectedNodeId,
} from "../organization/selectors";
import {
  AppStateResidentsStatus,
  ResidentBedStatus,
} from "../residentStatuses";
import { IAppState, SortDirection } from "../types";
import { authReady } from "../userProfile/selectors";
const isNoWifi = (status: WifiStatus) => {
  const validWifi =
    status !== null && status !== undefined && Object.keys(status).length > 1;
  return !validWifi;
};

const isUnplugged = (status: PowerStatus, deviceType: DeviceType) => {
  //being unplagged for biopatch is normal
  if (deviceType === DeviceType.biopatch) return false;
  if (!status) return false;
  return status.connected !== undefined ? !status.connected : false;
};

const isBatteryLow = (power: PowerStatus) => {
  return power && power.batteryLevel >= 5 && power.batteryLevel < 30;
};

const isBatteryEmpty = (power: PowerStatus) => {
  return power && power.batteryLevel >= 0 && power.batteryLevel < 5;
};

export const devicesByResidentIdCreator = () => {
  return createSelector(
    (state: IAppState) => devicesSlicedBySelectedNode(state),
    (_: IAppState, residentsId: string) => residentsId,
    (_: IAppState, __: string, emptyObj: Device[]) => emptyObj,
    (devices, residentsId, emptyObj) => {
      const result = devices.filter(
        (device) => device?.residentId === residentsId
      );
      return result.length > 0 ? result : emptyObj;
    }
  );
};

export const avaliableDevicesOnNode = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (_: IAppState, type: string) => type,
  (_: IAppState, __: string, nodeId: number) => nodeId,

  (devices, type, nodeId) => {
    const filtered = (device: Device) => {
      return (
        device.type === type &&
        device.nodeId === nodeId &&
        (type === DeviceType.userTag
          ? device.userId === undefined
          : device?.residentId === undefined)
      );
    };
    return Object.values(devices).filter(filtered);
  }
);

export const avaliableDevicesOnNodeCreator = () => {
  return createSelector(
    (state: IAppState): DeviceData => state.devices.devices,
    (_: IAppState, type: string) => type,
    (_: IAppState, __: string, nodeId: number) => nodeId,
    (devices, type, nodeId) => {
      const filtered = (device: Device) => {
        if (type !== "")
          return device.type !== type
            ? false
            : device.nodeId !== nodeId
            ? false
            : device?.residentId === undefined;
        return device?.residentId === undefined;
      };
      return Object.values(devices).filter(filtered);
    }
  );
};

export const getDevicesByIds = createSelector(
  (devices: DeviceData): DeviceData => devices,
  (_: DeviceData, ids: string[]) => ids,
  (devices, ids) => {
    const filtered = (device: Device) => {
      return ids.indexOf(device.id) !== -1;
    };
    return Object.values(devices).filter(filtered);
  }
);

export const areDevicesConnectedByIds = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (_: IAppState, ids: string[]) => ids,
  (devices, ids) => {
    const filtered = (device: Device) => {
      return ids.indexOf(device.id) !== -1;
    };
    return Object.values(devices)
      .filter(filtered)
      .some((el) =>
        el.type === DeviceType.userTag
          ? el.userId !== undefined
          : el?.residentId !== undefined
      );
  }
);

export const devicesSlicedBySelectedNode = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (devices, organizationNodes, userSelectedNodeId, currentUserBaseNode) => {
    if (userSelectedNodeId === currentUserBaseNode)
      return Object.values(devices);
    const isUnderNode = (nodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(organizationNodes, nodeId, false);
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return Object.values(devices).filter((dev: Device) =>
      isUnderNode(dev.nodeId, userSelectedNodeId)
    );
  }
);

export const devicesSlicedBySelectedENDNode = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedENDNodeId,
  currentUserBaseNode,
  (devices, organizationNodes, userSelectedNodeId, currentUserBaseNode) => {
    if (userSelectedNodeId === currentUserBaseNode)
      return Object.values(devices);
    const isUnderNode = (nodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(organizationNodes, nodeId, false);
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return Object.values(devices).filter((dev: Device) =>
      isUnderNode(dev.nodeId, userSelectedNodeId)
    );
  }
);

export const devicesSlicedBySelectedMonitorNode = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userMonitorNodeId,
  currentUserBaseNode,
  (devices, organizationNodes, userSelectedNodeId, currentUserBaseNode) => {
    if (userSelectedNodeId === currentUserBaseNode)
      return Object.values(devices);
    const isUnderNode = (nodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(organizationNodes, nodeId, false);
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return Object.values(devices).filter((dev: Device) =>
      isUnderNode(dev.nodeId, userSelectedNodeId)
    );
  }
);

export const devicesFiltered = createSelector(
  (state: IAppState): AppStateDevicesStatus => state.devicesStatuses,
  devicesSlicedBySelectedNode,
  (state: IAppState): AppStateResidentsStatus => state.residentStatuses,
  (_: IAppState, filters: DeviceFilterMap) => filters,
  (_: IAppState, __: DeviceFilterMap, type: DeviceType) => type,
  (
    devicesStatuses,
    devices: Device[],
    residentStatuses: AppStateResidentsStatus,
    filters: DeviceFilterMap,
    type
  ) => {
    return devices
      .filter((el) => el.type === type)
      .filter(
        filterCondition(filters, devicesStatuses, residentStatuses, type)
      );
  }
);
export const vitalsToDisplay = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (state: IAppState): DeviceFilterMap => state.devices.settingsVitals.filters,
  (state: IAppState): string => state.devices.settingsVitals.searchValue,
  (state: IAppState): DevicesSorting => state.devices.settingsVitals.sort,
  (state: IAppState): AppStateDevicesStatus => state.devicesStatuses,
  (state: IAppState): AppStateResidentsStatus => state.residentStatuses,
  (
    devices,
    organizationNodes,
    userSelectedNodeId,
    currentUserBaseNode,
    filters,
    searchValue,
    sort,
    devicesStatuses,
    residentStatuses
  ) => {
    console.log("vitalsToDisplay");
    const isUserUnderNode = (nodeId: number, maybeTop: number) => {
      if (userSelectedNodeId === currentUserBaseNode) return true;
      const selectedNodePath = pathToNode(organizationNodes, nodeId, false);
      if (selectedNodePath.length === 0) {
        //no org structure
        return nodeId === maybeTop;
      }
      return selectedNodePath.some((el) => el.id === maybeTop);
    };

    const sortById = (a: Device, b: Device): number => {
      if (a.id < b.id) {
        return sort.direction === "ASC" ? -1 : 1;
      }
      if (a.id > b.id) {
        return sort.direction === "ASC" ? 1 : -1;
      }
      return 0;
    };

    const sortCondition = (a: Device, b: Device): number => {
      if (sort.type === DevicesSortType.Id) {
        // if (a?.room && !b?.room) return -1;
        // if (b?.room && !a.room) return 1;
        // if (!b?.room && !a.room) return 0;
        return sortById(a, b);
      }
      let val = 0;
      if (a?.room && !b?.room) return -1;
      if (b?.room && !a.room) return 1;
      if (!b?.room && !a.room) return 0;
      if (Number.parseInt(a?.room) < Number.parseInt(b?.room)) {
        val = -1;
      }
      if (Number.parseInt(a?.room) > Number.parseInt(b?.room)) {
        val = 1;
      }
      if (a?.room === b?.room) {
        if (a?.bed?.toLowerCase() < b?.bed?.toLowerCase()) {
          val = -1;
        }
        if (a?.bed?.toLowerCase() > b?.bed?.toLowerCase()) {
          val = 1;
        }
      }
      return sort.direction === SortDirection.ASC ? val : val * -1;
    };
    const sortByBlock = (a: Device, b: Device): number => {
      const aBlock = nodeById(organizationNodes, a.nodeId);
      const bBlock = nodeById(organizationNodes, b.nodeId);
      const aTitle = aBlock.title;
      const bTitle = bBlock.title;

      if (aTitle?.toLowerCase() < bTitle?.toLowerCase()) {
        return sort.direction === "ASC" ? -1 : 1;
      }
      if (aTitle?.toLowerCase() > bTitle?.toLowerCase()) {
        return sort.direction === "ASC" ? 1 : -1;
      }
      return sortById(a, b);
    };

    return Object.values(devices)
      .filter((el: Device) => el.type === DeviceType.vitals)
      .filter((device: Device) =>
        isUserUnderNode(
          device.nodeId,
          userSelectedNodeId ? userSelectedNodeId : currentUserBaseNode
        )
      )
      .filter(
        filterCondition(
          filters,
          devicesStatuses,
          residentStatuses,
          DeviceType.vitals
        )
      )

      .sort(sort.type !== DevicesSortType.Block ? sortCondition : sortByBlock);
  }
);

export const biopatchToDisplay = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (state: IAppState): DeviceFilterMap => state.devices.settingsBiopatch.filters,
  (state: IAppState): string => state.devices.settingsBiopatch.searchValue,
  (state: IAppState): DevicesSorting => state.devices.settingsBiopatch.sort,
  (state: IAppState): AppStateDevicesStatus => state.devicesStatuses,
  (state: IAppState): AppStateResidentsStatus => state.residentStatuses,
  (
    devices,
    organizationNodes,
    userSelectedNodeId,
    currentUserBaseNode,
    filters,
    searchValue,
    sort,
    devicesStatuses,
    residentStatuses
  ) => {
    return devicesToDisplay(
      DeviceType.biopatch,
      organizationNodes,
      sort,
      devices,
      userSelectedNodeId,
      currentUserBaseNode,
      filters,
      devicesStatuses,
      residentStatuses,
      searchValue
    );
  }
);

export const watchToDisplay = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (state: IAppState): DeviceFilterMap => state.devices.settingsWatch.filters,
  (state: IAppState): string => state.devices.settingsWatch.searchValue,
  (state: IAppState): DevicesSorting => state.devices.settingsWatch.sort,
  (state: IAppState): AppStateDevicesStatus => state.devicesStatuses,
  (state: IAppState): AppStateResidentsStatus => state.residentStatuses,
  (
    devices,
    organizationNodes,
    userSelectedNodeId,
    currentUserBaseNode,
    filters,
    searchValue,
    sort,
    devicesStatuses,
    residentStatuses
  ) => {
    return devicesToDisplay(
      DeviceType.watch,
      organizationNodes,
      sort,
      devices,
      userSelectedNodeId,
      currentUserBaseNode,
      filters,
      devicesStatuses,
      residentStatuses,
      searchValue
    );
  }
);

export const tagToDisplay = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (state: IAppState): DeviceFilterMap => state.devices.settingsUserTag.filters,
  (state: IAppState): string => state.devices.settingsUserTag.searchValue,
  (state: IAppState): DevicesSorting => state.devices.settingsUserTag.sort,
  (state: IAppState): AppStateDevicesStatus => state.devicesStatuses,
  (state: IAppState): AppStateResidentsStatus => state.residentStatuses,
  (
    devices,
    organizationNodes,
    userSelectedNodeId,
    currentUserBaseNode,
    filters,
    searchValue,
    sort,
    devicesStatuses,
    residentStatuses
  ) => {
    return devicesToDisplay(
      DeviceType.userTag,
      organizationNodes,
      sort,
      devices,
      userSelectedNodeId,
      currentUserBaseNode,
      filters,
      devicesStatuses,
      residentStatuses,
      searchValue
    );
  }
);

const devicesToDisplay = (
  deviceType: DeviceType,
  organizationNodes: OrganizationNode[],
  sort: DevicesSorting,
  devices: DeviceData,
  userSelectedNodeId: number,
  currentUserBaseNode: number,
  filters: DeviceFilterMap,
  devicesStatuses: AppStateDevicesStatus,
  residentStatuses: AppStateResidentsStatus,
  searchValue: string
) => {
  const isUserUnderNode = (nodeId: number, maybeTop: number) => {
    if (userSelectedNodeId === currentUserBaseNode) return true;
    const selectedNodePath = pathToNode(organizationNodes, nodeId, false);
    if (selectedNodePath.length === 0) {
      //no org structure
      return nodeId === maybeTop;
    }
    return selectedNodePath.some((el) => el.id === maybeTop);
  };
  const sortByBlock = (a: Device, b: Device): number => {
    const aBlock = nodeById(organizationNodes, a.nodeId);
    const bBlock = nodeById(organizationNodes, b.nodeId);
    const aTitle = aBlock.title;
    const bTitle = bBlock.title;

    if (aTitle?.toLowerCase() < bTitle?.toLowerCase()) {
      return sort.direction === "ASC" ? -1 : 1;
    }
    if (aTitle?.toLowerCase() > bTitle?.toLowerCase()) {
      return sort.direction === "ASC" ? 1 : -1;
    }
    return sortById(a, b);
  };
  const sortById = (a: Device, b: Device): number => {
    if (a.id < b.id) {
      return sort.direction === "ASC" ? -1 : 1;
    }
    if (a.id > b.id) {
      return sort.direction === "ASC" ? 1 : -1;
    }
    return 0;
  };
  return Object.values(devices)
    .filter((el: Device) => el.type === deviceType)
    .filter((device: Device) =>
      isUserUnderNode(
        device.nodeId,
        userSelectedNodeId ? userSelectedNodeId : currentUserBaseNode
      )
    )
    .filter(
      filterCondition(filters, devicesStatuses, residentStatuses, deviceType)
    )

    .sort(sort.type === DevicesSortType.Id ? sortById : sortByBlock);
};

export const organizationHasBiopatch = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (devices) => {
    return (
      Object.values(devices).filter((el) => el.type === DeviceType.biopatch)
        .length > 0
    );
  }
);
export const organizationHasWatch = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (devices) => {
    return (
      Object.values(devices).filter((el) => el.type === DeviceType.watch)
        .length > 0
    );
  }
);

export const organizationHasUserTag = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (devices) => {
    return (
      Object.values(devices).filter((el) => el.type === DeviceType.userTag)
        .length > 0
    );
  }
);

export const organizationHasVitals = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (devices) => {
    return (
      Object.values(devices).filter((el) => el.type === DeviceType.vitals)
        .length > 0
    );
  }
);

export const devicessConvertToArray = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (devices) => {
    return Object.keys(devices).map((key) => devices[key]);
  }
);
export const vitalsSlicedBySelectedNode = createSelector(
  devicessConvertToArray,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (devices, organizationNodes, userSelectedNodeId, currentUserBaseNode) => {
    if (userSelectedNodeId === currentUserBaseNode)
      return devices.filter((el) => el.type === DeviceType.vitals);
    const isResidentUnderNode = (residentNodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(
        organizationNodes,
        residentNodeId,
        false
      );
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return devices
      .filter((el) => el.type === DeviceType.vitals)
      .filter((resident: Device) =>
        isResidentUnderNode(resident.nodeId, userSelectedNodeId)
      );
  }
);

export const biopatchSlicedBySelectedNode = createSelector(
  devicessConvertToArray,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (devices, organizationNodes, userSelectedNodeId, currentUserBaseNode) => {
    if (userSelectedNodeId === currentUserBaseNode)
      return devices.filter((el) => el.type === DeviceType.biopatch);
    const isResidentUnderNode = (residentNodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(
        organizationNodes,
        residentNodeId,
        false
      );
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return devices
      .filter((el) => el.type === DeviceType.biopatch)
      .filter((resident: Device) =>
        isResidentUnderNode(resident.nodeId, userSelectedNodeId)
      );
  }
);

export const watchSlicedBySelectedNode = createSelector(
  devicessConvertToArray,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (devices, organizationNodes, userSelectedNodeId, currentUserBaseNode) => {
    if (userSelectedNodeId === currentUserBaseNode)
      return devices.filter((el) => el.type === DeviceType.watch);
    const isResidentUnderNode = (residentNodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(
        organizationNodes,
        residentNodeId,
        false
      );
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return devices
      .filter((el) => el.type === DeviceType.watch)
      .filter((resident: Device) =>
        isResidentUnderNode(resident.nodeId, userSelectedNodeId)
      );
  }
);
export const tagSlicedBySelectedNode = createSelector(
  devicessConvertToArray,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  userSelectedNodeId,
  currentUserBaseNode,
  (devices, organizationNodes, userSelectedNodeId, currentUserBaseNode) => {
    if (userSelectedNodeId === currentUserBaseNode)
      return devices.filter((el) => el.type === DeviceType.userTag);
    const isResidentUnderNode = (residentNodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(
        organizationNodes,
        residentNodeId,
        false
      );
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    return devices
      .filter((el) => el.type === DeviceType.userTag)
      .filter((resident: Device) =>
        isResidentUnderNode(resident.nodeId, userSelectedNodeId)
      );
  }
);

export const vitalsSelectedCreator = () => {
  return createSelector(
    (state: IAppState): string[] =>
      state.devices.settingsVitals.selectedDevices,
    (_: IAppState, deviceId: string): string => deviceId,
    (selectded, deviceId) => {
      return selectded.indexOf(deviceId) !== -1;
    }
  );
};

export const biopatchSelectedCreator = () => {
  return createSelector(
    (state: IAppState): string[] =>
      state.devices.settingsBiopatch.selectedDevices,
    (_: IAppState, deviceId: string): string => deviceId,
    (selectded, deviceId) => {
      return selectded.indexOf(deviceId) !== -1;
    }
  );
};

export const watchSelectedCreator = () => {
  return createSelector(
    (state: IAppState): string[] => state.devices.settingsWatch.selectedDevices,
    (_: IAppState, deviceId: string): string => deviceId,
    (selectded, deviceId) => {
      return selectded.indexOf(deviceId) !== -1;
    }
  );
};

export const tagSelectedCreator = () => {
  return createSelector(
    (state: IAppState): string[] =>
      state.devices.settingsUserTag.selectedDevices,
    (_: IAppState, deviceId: string): string => deviceId,
    (selectded, deviceId) => {
      return selectded.indexOf(deviceId) !== -1;
    }
  );
};

export const isRoomBedAvaliable = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (state: IAppState): OrganizationNode[] => state.organization.nodes,
  (_: IAppState, newDeviceNodeId: number) => newDeviceNodeId,
  (_: IAppState, __: number, newRoom: string) => newRoom,
  (_: IAppState, __: number, ____: string, newBed: string) => newBed,
  (devices, organizationNodes, newDeviceNodeId, newRoom, newBed) => {
    if (
      newDeviceNodeId === undefined ||
      newRoom === undefined ||
      newBed === undefined
    )
      return false;
    const devicesBuilding = pathToNode(
      organizationNodes,
      newDeviceNodeId,
      false
    )[0];

    const isUserUnderNodeAndVitals = (nodeId: number, maybeTop: number) => {
      const selectedNodePath = pathToNode(organizationNodes, nodeId, false);
      return selectedNodePath.some((el) => el.id === maybeTop);
    };
    const devicesOnTheSameNode = Object.values(devices).filter(
      (dev: Device) =>
        isUserUnderNodeAndVitals(dev.nodeId, devicesBuilding.id) &&
        dev.type === DeviceType.vitals
    );

    return devicesOnTheSameNode.some(
      (d: Device) => d.room === newRoom && d.bed === newBed
    );
  }
);

export const devicesReady = createSelector(
  authReady,
  (state: IAppState): boolean => state.devices.isLoading,
  (state: IAppState): boolean => state.devicesStatuses.isLoading,
  (state: IAppState): boolean => state.healthCheck.residentServiceError,
  (mainLoaded, isLoading, isLoadingStatuses, residentServiceError) => {
    return !mainLoaded
      ? false
      : !isLoading && !isLoadingStatuses && residentServiceError === false;
  }
);

export const deviceHasProblems = createSelector(
  (state: IAppState): DeviceData => state.devices.devices,
  (state: IAppState): DevicesStatusMap =>
    state.devicesStatuses.activityStatuses,
  (_: IAppState, deviceId: string): string => deviceId,
  (_: IAppState, __: string, deviceType: DeviceType): DeviceType => deviceType,
  (devices, activityStatuses, deviceId, deviceType) => {
    const device = devices[deviceId];
    if (
      deviceType === DeviceType.vitals &&
      (device.room === undefined || device.bed === undefined)
    )
      return true;
    if (
      !activityStatuses[deviceId] ||
      !(activityStatuses[deviceId] as ActivityStatus).isActive
    )
      return true;

    return false;
  }
);

export const isSelectionModeVitals = createSelector(
  (state: IAppState): string[] => state.devices.settingsVitals.selectedDevices,
  (selectedDevices) => {
    return selectedDevices.length > 0;
  }
);

export const isSelectionModeBiopatch = createSelector(
  (state: IAppState): string[] =>
    state.devices.settingsBiopatch.selectedDevices,
  (selectedDevices) => {
    return selectedDevices.length > 0;
  }
);

export const isSelectionModeWatch = createSelector(
  (state: IAppState): string[] => state.devices.settingsWatch.selectedDevices,
  (selectedDevices) => {
    return selectedDevices.length > 0;
  }
);
export const isSelectionModeTag = createSelector(
  (state: IAppState): string[] => state.devices.settingsUserTag.selectedDevices,
  (selectedDevices) => {
    return selectedDevices.length > 0;
  }
);

const filterCondition = (
  filters: DeviceFilterMap,
  devicesStatuses: AppStateDevicesStatus,
  residentStatuses: AppStateResidentsStatus,
  type: DeviceType,
  logic = "AND"
) => {
  const condition = (el: Device) => {
    let meetAnycondition = false;
    let meetAndCondition = undefined;
    if (
      Object.values(filters).every((value) => {
        return !value;
      })
    )
      return true;
    const activityStatus = devicesStatuses.activityStatuses[
      el.id
    ] as ActivityStatus;
    const pirStatus = devicesStatuses.sensorsStatuses[el.id] as SensorsStatus;
    const pirValid = pirStatus && pirStatus?.pir && pirStatus?.pir?.isConnected;
    const power = devicesStatuses.powerStatuses[el.id] as PowerStatus;
    if (filters[DeviceFilter.ACTIVE])
      meetAnycondition = meetAnycondition || activityStatus?.isActive;
    if (filters[DeviceFilter.NOT_ACTIVE])
      meetAnycondition = meetAnycondition || !activityStatus?.isActive;
    if (filters[DeviceFilter.ACTIVE] || filters[DeviceFilter.NOT_ACTIVE])
      meetAndCondition =
        filters[DeviceFilter.ACTIVE] && filters[DeviceFilter.NOT_ACTIVE]
          ? true
          : filters[DeviceFilter.ACTIVE]
          ? activityStatus?.isActive
          : !activityStatus?.isActive;

    if (type !== DeviceType.userTag) {
      if (filters[DeviceFilter.RESIDENT_NO])
        meetAnycondition =
          meetAnycondition ||
          el?.residentId === undefined ||
          el?.residentId === "";
      if (filters[DeviceFilter.RESIDENT_YES])
        meetAnycondition =
          meetAnycondition ||
          (el?.residentId !== undefined && el?.residentId !== "");

      if (
        filters[DeviceFilter.RESIDENT_YES] ||
        filters[DeviceFilter.RESIDENT_NO]
      )
        meetAndCondition =
          (meetAndCondition === undefined ? true : meetAndCondition) &&
          (filters[DeviceFilter.RESIDENT_YES] &&
          filters[DeviceFilter.RESIDENT_NO]
            ? true
            : filters[DeviceFilter.RESIDENT_YES]
            ? el?.residentId !== undefined
            : el?.residentId === undefined);
    } else if (type === DeviceType.userTag) {
      if (filters[DeviceFilter.RESIDENT_NO])
        meetAnycondition =
          meetAnycondition || el.userId === undefined || el.userId === "";
      if (filters[DeviceFilter.RESIDENT_YES])
        meetAnycondition =
          meetAnycondition || (el.userId !== undefined && el.userId !== "");

      if (
        filters[DeviceFilter.RESIDENT_YES] ||
        filters[DeviceFilter.RESIDENT_NO]
      )
        meetAndCondition =
          (meetAndCondition === undefined ? true : meetAndCondition) &&
          (filters[DeviceFilter.RESIDENT_YES] &&
          filters[DeviceFilter.RESIDENT_NO]
            ? true
            : filters[DeviceFilter.RESIDENT_YES]
            ? el.userId !== undefined
            : el.userId === undefined);
    }
    if (filters[DeviceFilter.ROOM_BED_YES])
      meetAnycondition = meetAnycondition || el.room !== undefined;
    if (filters[DeviceFilter.ROOM_BED_NO])
      meetAnycondition = meetAnycondition || el.room === undefined;
    if (filters[DeviceFilter.ROOM_BED_YES] || filters[DeviceFilter.ROOM_BED_NO])
      meetAndCondition =
        (meetAndCondition === undefined ? true : meetAndCondition) &&
        (filters[DeviceFilter.ROOM_BED_YES] && filters[DeviceFilter.ROOM_BED_NO]
          ? true
          : filters[DeviceFilter.ROOM_BED_YES]
          ? el.room !== undefined
          : el.room === undefined);

    if (filters[DeviceFilter.PUMP_YES])
      meetAnycondition = meetAnycondition || el.hasPump;
    if (filters[DeviceFilter.PUMP_NO])
      meetAnycondition = meetAnycondition || !el.hasPump;
    if (filters[DeviceFilter.PUMP_YES] || filters[DeviceFilter.PUMP_NO])
      meetAndCondition =
        (meetAndCondition === undefined ? true : meetAndCondition) &&
        (filters[DeviceFilter.PUMP_YES] && filters[DeviceFilter.PUMP_NO]
          ? true
          : filters[DeviceFilter.PUMP_YES]
          ? el.hasPump
          : !el.hasPump);
    if (filters[DeviceFilter.PIR_YES])
      meetAnycondition = meetAnycondition || pirValid;
    if (filters[DeviceFilter.PIR_NO])
      meetAnycondition = meetAnycondition || !pirValid;
    if (filters[DeviceFilter.PIR_YES] || filters[DeviceFilter.PIR_NO])
      meetAndCondition =
        (meetAndCondition === undefined ? true : meetAndCondition) &&
        (filters[DeviceFilter.PIR_YES] && filters[DeviceFilter.PIR_NO]
          ? true
          : filters[DeviceFilter.PIR_YES]
          ? pirValid
          : !pirValid);
    if (filters[DeviceFilter.UNPLUGGED]) {
      meetAnycondition =
        meetAnycondition ||
        isUnplugged(power, el.type) ||
        activityStatus?.status === WhyNotActive.NO_BATTERY ||
        activityStatus?.status === WhyNotActive.UNPLUGGED_AND_NO_BATTERY;
      meetAndCondition = meetAnycondition;
    }
    if (filters[DeviceFilter.MAINTENANCE]) {
      meetAnycondition = meetAnycondition || (power && power?.failure);
      meetAndCondition = meetAnycondition;
    }
    const badBlue =
      ((activityStatus === undefined || !activityStatus?.isActive) &&
        (power === undefined || power?.batteryLevel >= 0)) ||
      (!activityStatus?.isActive &&
        activityStatus?.status === WhyNotActive.NO_BLUETOOTH);
    const batteryLow =
      type === DeviceType.biopatch
        ? isBatteryLow(power) && !badBlue
        : isBatteryLow(power);
    if (filters[DeviceFilter.BatteryLevelLow]) {
      meetAnycondition = meetAnycondition || batteryLow;
      meetAndCondition = meetAnycondition;
    }

    if (filters[DeviceFilter.BatteryLevelEmpty]) {
      const baseStatus =
        isBatteryEmpty(power) ||
        activityStatus?.status === WhyNotActive.NO_BATTERY ||
        activityStatus?.status === WhyNotActive.UNPLUGGED_AND_NO_BATTERY;
      meetAnycondition =
        meetAnycondition || type === DeviceType.biopatch
          ? badBlue
            ? false
            : baseStatus
          : baseStatus;
      meetAndCondition = meetAnycondition;
    }
    const wifi = devicesStatuses.wifiStatuses[el.id] as WifiStatus;
    if (filters[DeviceFilter.LOW_WIFI]) {
      meetAnycondition =
        meetAnycondition || (wifi as WifiStatus)?.rssiDbm <= VITALS_LOW_RSSI;
      meetAndCondition = meetAnycondition;
    }
    if (filters[DeviceFilter.NO_WIFI]) {
      meetAnycondition =
        meetAnycondition ||
        (isNoWifi(wifi) && activityStatus?.status === WhyNotActive.NO_WIFI);
      meetAndCondition = meetAnycondition;
    }
    if (filters[DeviceFilter.NO_BLUETOOTH]) {
      meetAnycondition = meetAnycondition || badBlue;
      meetAndCondition = meetAnycondition;
    }
    const onoff = devicesStatuses.onOffStatuses[el.id] as OnOffStatus;
    if (filters[DeviceFilter.TURN_OFF]) {
      meetAnycondition = meetAnycondition || !onoff?.isOn;
      meetAndCondition = meetAnycondition;
    }
    const userNeaby = devicesStatuses.userNearbyStatuses[
      el.id
    ] as UserNearbyStatus;

    if (userNeaby?.nearBed?.length > 0 && el?.residentId) {
      const occupancyStatus = devicesStatuses.occupancyStatuses[
        el.id
      ] as OccupancyStatus;
      const bedStatus = residentStatuses.bedStatuses[
        el?.residentId
      ] as ResidentBedStatus;
      const status = occupancyStatus
        ? { ...bedStatus, value: occupancyStatus.value }
        : bedStatus;
      if (status && status?.value !== BedStatusValue.INIT) {
        if (filters[DeviceFilter.USER_NEARBY_IN_BED]) {
          meetAnycondition =
            meetAnycondition || status?.value === BedStatusValue.IN_BED;
          meetAndCondition = meetAnycondition;
        } else if (filters[DeviceFilter.USER_NEARBY_OUT_BED]) {
          meetAnycondition =
            meetAnycondition || status?.value === BedStatusValue.OUT_OF_BED;
          meetAndCondition = meetAnycondition;
        }
      }
    }
    if (userNeaby?.nearRoom?.length > 0 && el?.residentId) {
      if (filters[DeviceFilter.USER_NEARBY_ROOM]) {
        meetAnycondition = meetAnycondition || true;
        meetAndCondition = meetAnycondition;
      }
    }
    return logic === "OR" ? meetAnycondition : meetAndCondition;
  };
  return condition;
};

export const getDevicesByFilterTypesCreator = () => {
  return createSelector(
    (state: IAppState): DeviceData => state.devices.devices,
    organizationNodes,
    userSelectedNodeId,
    currentUserBaseNode,
    (state: IAppState): AppStateDevicesStatus => state.devicesStatuses,
    (state: IAppState): AppStateResidentsStatus => state.residentStatuses,
    (__: IAppState, deviceType: DeviceType): DeviceType => deviceType,
    (
      devices: DeviceData,
      organizationNodes: OrganizationNode[],
      userSelectedNodeId: number,
      currentUserBaseNode: number,
      devicesStatuses: AppStateDevicesStatus,
      residentStatuses: AppStateResidentsStatus,
      deviceType: DeviceType
    ) => {
      const isUserUnderNode = (nodeId: number, maybeTop: number) => {
        if (userSelectedNodeId === currentUserBaseNode) return true;
        const selectedNodePath = pathToNode(organizationNodes, nodeId, false);
        if (selectedNodePath.length === 0) {
          //no org structure
          return nodeId === maybeTop;
        }
        return selectedNodePath.some((el) => el.id === maybeTop);
      };
      const devicesCheck = Object.values(devices)
        .filter((el: Device) => el.type === deviceType)
        .filter((device: Device) =>
          isUserUnderNode(
            device.nodeId,
            userSelectedNodeId ? userSelectedNodeId : currentUserBaseNode
          )
        );
      const byFilters = {};
      Object.values(DeviceFilter).map((el) => (byFilters[el] = 0));

      devicesCheck.map((el: Device) => {
        const activityStatus = devicesStatuses.activityStatuses[
          el.id
        ] as ActivityStatus;
        if (activityStatus?.isActive) {
          byFilters[DeviceFilter.ACTIVE] = byFilters[DeviceFilter.ACTIVE] + 1;
        } else {
          byFilters[DeviceFilter.NOT_ACTIVE] =
            byFilters[DeviceFilter.NOT_ACTIVE] + 1;
        }

        if (deviceType === DeviceType.userTag) {
          if (el?.userId && el?.userId !== "" && el?.userId !== undefined) {
            byFilters[DeviceFilter.RESIDENT_YES] =
              byFilters[DeviceFilter.RESIDENT_YES] + 1;
          } else {
            byFilters[DeviceFilter.RESIDENT_NO] =
              byFilters[DeviceFilter.RESIDENT_NO] + 1;
          }
        } else {
          if (
            el?.residentId &&
            el?.residentId !== "" &&
            el?.residentId !== undefined
          ) {
            byFilters[DeviceFilter.RESIDENT_YES] =
              byFilters[DeviceFilter.RESIDENT_YES] + 1;
          } else {
            byFilters[DeviceFilter.RESIDENT_NO] =
              byFilters[DeviceFilter.RESIDENT_NO] + 1;
          }
        }
        const power = devicesStatuses.powerStatuses[el.id] as PowerStatus;
        const badBlue =
          ((activityStatus === undefined || !activityStatus?.isActive) &&
            (power === undefined || power?.batteryLevel >= 0)) ||
          (!activityStatus?.isActive &&
            activityStatus?.status === WhyNotActive.NO_BLUETOOTH);
        if (isBatteryLow(power)) {
          if (
            badBlue &&
            deviceType === DeviceType.biopatch //dont count;
          ) {
          } else
            byFilters[DeviceFilter.BatteryLevelLow] =
              byFilters[DeviceFilter.BatteryLevelLow] + 1;
        }
        //avoiding badass biopatch falls into lowBattery + no bluetooth
        if (
          isBatteryEmpty(power) ||
          activityStatus?.status === WhyNotActive.NO_BATTERY ||
          activityStatus?.status === WhyNotActive.UNPLUGGED_AND_NO_BATTERY
        ) {
          if (
            badBlue &&
            deviceType === DeviceType.biopatch //dont count;
          ) {
          } else
            byFilters[DeviceFilter.BatteryLevelEmpty] =
              byFilters[DeviceFilter.BatteryLevelEmpty] + 1;
        }
        if (
          power?.batteryCharging === true &&
          deviceType === DeviceType.userTag
        ) {
          byFilters[DeviceFilter.BATTERY_CHARGING] =
            byFilters[DeviceFilter.BATTERY_CHARGING] + 1;
        }
        if (power && power?.failure)
          byFilters[DeviceFilter.MAINTENANCE] =
            byFilters[DeviceFilter.MAINTENANCE] + 1;
        if (deviceType === DeviceType.vitals) {
          if (el.room !== undefined)
            byFilters[DeviceFilter.ROOM_BED_YES] =
              byFilters[DeviceFilter.ROOM_BED_YES] + 1;
          else
            byFilters[DeviceFilter.ROOM_BED_NO] =
              byFilters[DeviceFilter.ROOM_BED_NO] + 1;

          const pirStatus = devicesStatuses.sensorsStatuses[
            el.id
          ] as SensorsStatus;
          const pirValid =
            pirStatus && pirStatus?.pir && pirStatus?.pir?.isConnected;
          if (pirValid)
            byFilters[DeviceFilter.PIR_YES] =
              byFilters[DeviceFilter.PIR_YES] + 1;
          else
            byFilters[DeviceFilter.PIR_NO] = byFilters[DeviceFilter.PIR_NO] + 1;
          if (el?.hasPump)
            byFilters[DeviceFilter.PUMP_YES] =
              byFilters[DeviceFilter.PUMP_YES] + 1;
          else
            byFilters[DeviceFilter.PUMP_NO] =
              byFilters[DeviceFilter.PUMP_NO] + 1;
          if (
            isUnplugged(power, el.type) ||
            activityStatus?.status === WhyNotActive.NO_BATTERY ||
            activityStatus?.status === WhyNotActive.UNPLUGGED_AND_NO_BATTERY
          )
            byFilters[DeviceFilter.UNPLUGGED] =
              byFilters[DeviceFilter.UNPLUGGED] + 1;
          const wifi = devicesStatuses.wifiStatuses[el.id] as WifiStatus;
          if (isNoWifi(wifi) && activityStatus?.status === WhyNotActive.NO_WIFI)
            byFilters[DeviceFilter.NO_WIFI] =
              byFilters[DeviceFilter.NO_WIFI] + 1;

          if ((wifi as WifiStatus)?.rssiDbm <= VITALS_LOW_RSSI)
            byFilters[DeviceFilter.LOW_WIFI] =
              byFilters[DeviceFilter.LOW_WIFI] + 1;
          const onoff = devicesStatuses.onOffStatuses[el.id] as OnOffStatus;
          if (!onoff?.isOn)
            byFilters[DeviceFilter.TURN_OFF] =
              byFilters[DeviceFilter.TURN_OFF] + 1;
          const userNeaby = devicesStatuses.userNearbyStatuses[
            el.id
          ] as UserNearbyStatus;

          if (userNeaby?.nearBed?.length > 0 && el?.residentId) {
            const occupancyStatus = devicesStatuses.occupancyStatuses[
              el.id
            ] as OccupancyStatus;
            const bedStatus = residentStatuses.bedStatuses[
              el?.residentId
            ] as ResidentBedStatus;
            const status = occupancyStatus
              ? { ...bedStatus, value: occupancyStatus.value }
              : bedStatus;
            if (!status || status?.value === BedStatusValue.INIT) return null;
            if (status.value === BedStatusValue.IN_BED)
              byFilters[DeviceFilter.USER_NEARBY_IN_BED] =
                byFilters[DeviceFilter.USER_NEARBY_IN_BED] + 1;
            else if (status.value === BedStatusValue.OUT_OF_BED)
              byFilters[DeviceFilter.USER_NEARBY_OUT_BED] =
                byFilters[DeviceFilter.USER_NEARBY_OUT_BED] + 1;
          }
          if (userNeaby?.nearRoom?.length > 0 && el?.residentId) {
            byFilters[DeviceFilter.USER_NEARBY_ROOM] =
              byFilters[DeviceFilter.USER_NEARBY_ROOM] + 1;
          }
        } else if (
          deviceType === DeviceType.biopatch ||
          deviceType === DeviceType.userTag
        ) {
          if (badBlue) {
            byFilters[DeviceFilter.NO_BLUETOOTH] =
              byFilters[DeviceFilter.NO_BLUETOOTH] + 1;
          }
        }
        return null;
      });
      return byFilters;
    }
  );
};
